import React, { useRef, useState, useEffect, useMemo, forwardRef, useImperativeHandle } from "react";
import { Stage, Layer, Image as KonvaImage, Rect, Text } from "react-konva";
import PopupForm from "../popup/Popup";
import './KonvaDrawing.css';

// 🚀 Global image cache to avoid reloading images on navigation
const imageCache = new Map();

const KonvaDrawing = forwardRef((props, ref) => {
  const {
    imageUrl,
    damagedParts = [],
    imageId,
    imageTitle,
    onNewDamageAdded,
    onBoxDeleted,
    onBoxUpdated,
    partsList = [],
    damagesList = [],
    suggestionsList = [],
    isDrawing,
    toggleDrawingMode,
    isReadOnly = false,
    vin = null,
    category = null,
    vlmRejected = [],
    showVlmMarks = true,
  } = props;

  const stageRef = useRef(null);
  const [image, setImage] = useState(null);
  const [scaleFactors, setScaleFactors] = useState({ scaleX: 1, scaleY: 1 });
  const [newBox, setNewBox] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [hoveredBox, setHoveredBox] = useState(null);
  const [hoveredRejected, setHoveredRejected] = useState(null);
  const [popupMode, setPopupMode] = useState("create");
  const [tempBox, setTempBox] = useState(null);

  const [showBoxSelector, setShowBoxSelector] = useState(false);
  const [hitBoxes, setHitBoxes] = useState([]);

  const stageWidth = 800;
  const stageHeight = 450;

  const colorMapping = {
    Eraflures: "blue",
    Enfoncement: "green",
    Corrosion: "orange",
    Ecaillement: "purple",
    Fissure: "yellow",
    Rayure: "pink",
    Deboitement: "brown",
    Absent: "gray",
    Corrompu: "black",
    Casse: "red",
    BDG: "cyan",
    Dommage: "red",
  };

  const imageBounds = { x: 0, y: 0, width: stageWidth, height: stageHeight };

  // ────────────────────────────────────────────────────────────
  // IMAGE LOADING
  // ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!imageUrl) return;

    const processImage = (img) => {
      setImage(img);
      setScaleFactors({
        scaleX: stageWidth / img.width,
        scaleY: stageHeight / img.height,
      });
    };

    if (imageCache.has(imageUrl)) {
      const cachedImg = imageCache.get(imageUrl);
      if (cachedImg.complete && cachedImg.naturalWidth > 0) {
        processImage(cachedImg);
        return;
      }
    }

    const img = new window.Image();
    img.crossOrigin = "Anonymous";
    img.src = imageUrl;
    img.onload = () => {
      imageCache.set(imageUrl, img);
      processImage(img);
    };
    img.onerror = () => console.error("Failed to load image:", imageUrl);
  }, [imageUrl]);

  // ────────────────────────────────────────────────────────────
  // BOXES DÉRIVÉES depuis damagedParts (plus besoin de initialBoxes/newBoxes)
  // ────────────────────────────────────────────────────────────

  const boxes = useMemo(() => {
    const { scaleX, scaleY } = scaleFactors;
    if (!scaleX || !scaleY) return [];

    const toBox = (part, bbox, bboxIndex, fromVlm) => ({
      id: part.id,
      bboxIndex,
      x: bbox[0] * scaleX,
      y: bbox[1] * scaleY,
      width: (bbox[2] - bbox[0]) * scaleX,
      height: (bbox[3] - bbox[1]) * scaleY,
      damageLabel: part.damage_type || "Dommage",
      isNew: part.isNew || false,
      fromVlm,
      details: { ...part },
    });
    const sameBox = (a, b) => a.every((v, i) => Math.abs(Number(v) - Number(b[i])) < 1);

    return (damagedParts || []).flatMap((part) => {
      const own = part.bounding_boxes || [];
      const isVlmRow = showVlmMarks && part.source === "vlm";
      const boxesOfPart = own.map((bbox, i) => toBox(part, bbox, i, isVlmRow));
      // Zones trouvées par le VLM sur une ligne YOLO : absentes de bounding_boxes
      const extra = (part.vlm?.boxes || [])
        .filter((vb) => Array.isArray(vb) && vb.length === 4 && !own.some((b) => sameBox(b, vb)))
        .map((vb, i) => toBox(part, vb, `vlm-${i}`, showVlmMarks));
      return [...boxesOfPart, ...extra];
    });
  }, [damagedParts, scaleFactors, showVlmMarks]);

  // Zones YOLO écartées par le VLM (absentes du résultat) : repérables sur l'image
  const rejectedBoxes = useMemo(() => {
    const { scaleX, scaleY } = scaleFactors;
    if (!scaleX || !scaleY) return [];
    return (vlmRejected || [])
      .filter((z) => Array.isArray(z?.bbox) && z.bbox.length === 4)
      .map((z, i) => ({
        key: `rej-${i}`,
        x: z.bbox[0] * scaleX,
        y: z.bbox[1] * scaleY,
        width: (z.bbox[2] - z.bbox[0]) * scaleX,
        height: (z.bbox[3] - z.bbox[1]) * scaleY,
        zone: z,
      }));
  }, [vlmRejected, scaleFactors]);

  // ────────────────────────────────────────────────────────────
  // HELPERS
  // ────────────────────────────────────────────────────────────

  const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

  const getAnnotatedImageAsBase64 = () => {
    if (!stageRef.current) return null;
    return stageRef.current.toDataURL({ mimeType: "image/jpeg", quality: 1, pixelRatio: 8 });
  };

  useImperativeHandle(ref, () => ({ getAnnotatedImageAsBase64 }));

  const isPointInBox = (x, y, box) => (
    x >= box.x && x <= box.x + box.width &&
    y >= box.y && y <= box.y + box.height
  );

  // ────────────────────────────────────────────────────────────
  // MOUSE HANDLERS (dessin)
  // ────────────────────────────────────────────────────────────

  const handleMouseDown = () => {
    if (!isDrawing) return;
    const stage = stageRef.current;
    const pos = stage.getPointerPosition();
    if (!pos) return;
    if (pos.x < imageBounds.x || pos.y < imageBounds.y ||
        pos.x > imageBounds.x + imageBounds.width || pos.y > imageBounds.y + imageBounds.height) return;

    setNewBox({
      x: clamp(pos.x, imageBounds.x, imageBounds.x + imageBounds.width),
      y: clamp(pos.y, imageBounds.y, imageBounds.y + imageBounds.height),
      width: 0, height: 0,
    });
  };

  const handleMouseMove = () => {
    if (!isDrawing || !newBox) return;
    const stage = stageRef.current;
    const pos = stage.getPointerPosition();
    if (!pos) return;
    setNewBox((prev) => ({
      ...prev,
      width: clamp(pos.x, imageBounds.x, imageBounds.x + imageBounds.width) - prev.x,
      height: clamp(pos.y, imageBounds.y, imageBounds.y + imageBounds.height) - prev.y,
    }));
  };

  const handleMouseUp = () => {
    if (isDrawing && newBox) {
      const { scaleX, scaleY } = scaleFactors;
      const tempBoxData = {
        x: newBox.x / scaleX,
        y: newBox.y / scaleY,
        width: newBox.width / scaleX,
        height: newBox.height / scaleY,
        damageLabel: "Nouveau Dommage",
        details: { part_name: "", damage_type: "", damage_prob: 100, price: 0, suggestion: "" },
      };
      setTempBox(tempBoxData);
      setPopupData(tempBoxData);
      setPopupMode("create");
      setShowPopup(true);
      setNewBox(null);
    }
    if (isDrawing && toggleDrawingMode) toggleDrawingMode();
  };

  // ────────────────────────────────────────────────────────────
  // CLICK HANDLERS (sélection de box)
  // ────────────────────────────────────────────────────────────

  const handleStageClick = () => {
    if (isDrawing || isReadOnly) return;
    const stage = stageRef.current;
    const pos = stage.getPointerPosition();
    if (!pos) return;

    const boxesUnderCursor = boxes.filter((box) => isPointInBox(pos.x, pos.y, box));
    if (boxesUnderCursor.length === 0) return;
    if (boxesUnderCursor.length === 1) { handleBoxClick(boxesUnderCursor[0]); return; }
    setHitBoxes(boxesUnderCursor);
    setShowBoxSelector(true);
  };

  const handleBoxPointerClick = (e) => {
    if (isReadOnly) return;
    e.cancelBubble = true;
    const stage = stageRef.current;
    const pos = stage?.getPointerPosition();
    if (!pos) return;

    const boxesUnderCursor = boxes.filter((b) => isPointInBox(pos.x, pos.y, b));
    if (boxesUnderCursor.length === 0) return;
    if (boxesUnderCursor.length === 1) { handleBoxClick(boxesUnderCursor[0]); return; }
    setHitBoxes(boxesUnderCursor);
    setShowBoxSelector(true);
  };

  const handleBoxClick = (box) => {
    if (isReadOnly) return;
    setPopupData({
      id: box.id,
      previousPartName: box.details.part_name.replace(/_/g, " ") || "",
      previousDamageType: box.details.damage_type || "",
      partName: box.details.part_name.replace(/_/g, " ") || "",
      damageType: box.details.damage_type || "",
      damageProb: box.details.damage_prob || 0,
      price: box.details.price || 0,
      suggestion: box.details.suggestion || "",
      imageTitle: imageTitle || "",
    });
    setPopupMode("edit");
    setShowPopup(true);
  };

  // ────────────────────────────────────────────────────────────
  // POPUP HANDLERS
  // ────────────────────────────────────────────────────────────

  const handleCancel = () => {
    setShowPopup(false);
    setPopupData(null);
    setTempBox(null);
  };

  const handlePopupSubmit = (damageData) => {
    console.log("[KonvaDrawing] handlePopupSubmit received:", damageData);
    
    if (popupMode === "create" && tempBox) {
      console.log("[KonvaDrawing] CREATE mode - calling onNewDamageAdded with price:", damageData.price);
      onNewDamageAdded(imageId, { ...damageData, x: tempBox.x, y: tempBox.y, width: tempBox.width, height: tempBox.height });
      setTempBox(null);
    } else if (popupMode === "edit") {
      console.log("[KonvaDrawing] EDIT mode - calling onBoxUpdated with price:", damageData.price);
      if (onBoxUpdated) {
        onBoxUpdated(popupData.id, {
          ...damageData,
          previousDamageType: popupData.previousDamageType,
          previousPartName: popupData.previousPartName,
        });
      }
    }
    setShowPopup(false);
    setPopupData(null);
  };

  const handlePopupDelete = () => {
    if (popupData && onBoxDeleted) {
      onBoxDeleted(popupData.id);
    }
    setShowPopup(false);
    setPopupData(null);
  };

  // ────────────────────────────────────────────────────────────
  // HOVER
  // ────────────────────────────────────────────────────────────

  const handleMouseEnter = (box) => setHoveredBox(box);
  const handleMouseLeave = () => setHoveredBox(null);

  // ────────────────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────────────────

  return (
    <div style={{ position: "relative" }}>

      {/* Sélecteur multi-box */}
      {showBoxSelector && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Plusieurs dommages détectés</h3>
            <p>Veuillez choisir le dommage :</p>
            {hitBoxes.map((box) => (
              <button
                key={`${box.id}-${box.bboxIndex}`}
                className="btn-secondary"
                style={{ display: "block", marginBottom: "8px" }}
                onClick={() => { setShowBoxSelector(false); setHitBoxes([]); handleBoxClick(box); }}
              >
                {box.details?.part_name?.replace(/_/g, " ") || "Pièce inconnue"}
                {" — "}
                {box.details?.damage_type || "Dommage"}
              </button>
            ))}
            <button className="btn-secondary" onClick={() => { setShowBoxSelector(false); setHitBoxes([]); }}>
              Annuler
            </button>
          </div>
        </div>
      )}

      <Stage
        width={stageWidth} height={stageHeight} ref={stageRef}
        onClick={handleStageClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        style={{ border: "1px solid #ccc", marginTop: "10px" }}
      >
        <Layer>
          {image && <KonvaImage image={image} x={0} y={0} width={stageWidth} height={stageHeight} />}

          {/* Toutes les boxes dérivées de damagedParts */}
          {boxes.map((box) => (
            <React.Fragment key={`${box.id}-${box.bboxIndex}`}>
              <Rect
                x={box.x} y={box.y} width={box.width} height={box.height}
                stroke={colorMapping[box.damageLabel] || colorMapping.Dommage}
                strokeWidth={2}
                fill="rgba(0, 0, 0, 0)"
                dash={box.isNew ? [4, 4] : box.fromVlm ? [8, 4] : undefined}
                onMouseEnter={() => handleMouseEnter(box)}
                onMouseLeave={handleMouseLeave}
                onClick={handleBoxPointerClick}
              />
              <Text
                listening={false}
                x={box.x} y={box.y - 20}
                text={box.fromVlm ? `VLM · ${box.damageLabel}` : box.damageLabel}
                fontSize={14}
                fill={colorMapping[box.damageLabel] || colorMapping.Dommage}
              />
            </React.Fragment>
          ))}

          {/* Zones écartées par le VLM : gris, pointillés */}
          {rejectedBoxes.map((box) => (
            <React.Fragment key={box.key}>
              <Rect
                x={box.x} y={box.y} width={box.width} height={box.height}
                stroke="#9ca3af" strokeWidth={2} dash={[3, 5]}
                fill="rgba(0, 0, 0, 0)"
                onMouseEnter={() => setHoveredRejected(box)}
                onMouseLeave={() => setHoveredRejected(null)}
              />
              <Text
                listening={false}
                x={box.x} y={box.y + box.height + 2}
                text={`VLM écarté · ${box.zone.damage_type || "Dommage"}`}
                fontSize={12}
                fill="#6b7280"
              />
            </React.Fragment>
          ))}

          {/* Box en cours de dessin */}
          {newBox && (
            <Rect
              x={newBox.x} y={newBox.y} width={newBox.width} height={newBox.height}
              stroke="blue" strokeWidth={2} dash={[4, 4]}
            />
          )}
        </Layer>
      </Stage>

      {showPopup && (
        <PopupForm
          mode={popupMode}
          onSubmit={handlePopupSubmit}
          onCancel={handleCancel}
          onDelete={popupMode === "edit" ? handlePopupDelete : null}
          initialData={popupData}
          partsList={partsList}
          damagesList={damagesList}
          suggestionsList={suggestionsList}
          vin={vin}
          category={category}
        />
      )}

      {hoveredBox && !isReadOnly && (
        <div style={{
          position: "absolute",
          top: hoveredBox.y + 30, left: hoveredBox.x + 20,
          backgroundColor: "rgba(0, 0, 0, 0.8)", color: "white",
          padding: "10px", borderRadius: "5px", pointerEvents: "none", zIndex: 10,
        }}>
          <p><strong>Dommage :</strong> {hoveredBox.details.damage_type || "N/A"}</p>
          <p><strong>Pièce :</strong> {hoveredBox.details.part_name?.replace(/_/g, " ") || "N/A"}</p>
          <p><strong>Probabilité :</strong> {hoveredBox.details.damage_prob || "N/A"}%</p>
          <p><strong>Suggestion :</strong> {hoveredBox.details.suggestion || "N/A"}</p>
          {hoveredBox.fromVlm && (
            <>
              <p style={{ marginTop: 6 }}><strong>🧠 Trouvé par le VLM</strong>
                {hoveredBox.details.vlm?.confiance != null && ` (confiance ${Math.round(hoveredBox.details.vlm.confiance * 100)}%)`}
              </p>
              {hoveredBox.details.vlm?.justification && (
                <p style={{ maxWidth: 320, whiteSpace: "normal" }}>{hoveredBox.details.vlm.justification}</p>
              )}
            </>
          )}
        </div>
      )}

      {hoveredRejected && !isReadOnly && (
        <div style={{
          position: "absolute",
          top: hoveredRejected.y + hoveredRejected.height + 20, left: hoveredRejected.x + 20,
          backgroundColor: "rgba(55, 65, 81, 0.92)", color: "white",
          padding: "10px", borderRadius: "5px", pointerEvents: "none", zIndex: 10, maxWidth: 340,
        }}>
          <p><strong>Écarté par le VLM :</strong> {hoveredRejected.zone.damage_type || "N/A"}</p>
          <p><strong>Pièce :</strong> {hoveredRejected.zone.part_name?.replace(/_/g, " ") || "N/A"}</p>
          {hoveredRejected.zone.yolo_prob != null && <p><strong>Probabilité YOLO :</strong> {hoveredRejected.zone.yolo_prob}%</p>}
          {hoveredRejected.zone.confiance != null && <p><strong>Confiance VLM :</strong> {Math.round(hoveredRejected.zone.confiance * 100)}%</p>}
          {(hoveredRejected.zone.justification || hoveredRejected.zone.motif) && (
            <p style={{ whiteSpace: "normal" }}>{hoveredRejected.zone.justification || hoveredRejected.zone.motif}</p>
          )}
        </div>
      )}
    </div>
  );
});

export default KonvaDrawing;